import React, { useState } from 'react';
import {
  BookOpen,
  Package,
  Layers,
  Scissors,
  QrCode,
  History,
  Archive,
  Search,
  Printer,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  ArrowRight,
  FileText,
  Sparkles,
  Zap,
  Tag,
  Copy,
  Plus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SystemManualsPage = () => {
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState('estoque');
  const [activeSection, setActiveSection] = useState('visao-geral');
  const [searchTerm, setSearchTerm] = useState('');

  const modules = [
    {
      id: 'estoque',
      name: 'Estoque, Produtos & Retalhos',
      icon: Package,
      status: 'Pronto',
      badgeColor: 'bg-emerald-100 text-emerald-800'
    },
    {
      id: 'levantamento',
      name: 'Levantamento & Croquis 2D',
      icon: FileText,
      status: 'Em breve',
      badgeColor: 'bg-gray-100 text-gray-600'
    },
    {
      id: 'projeto3d',
      name: 'Projeto 3D & Apresentação',
      icon: Sparkles,
      status: 'Em breve',
      badgeColor: 'bg-gray-100 text-gray-600'
    },
    {
      id: 'orcamentos',
      name: 'Orçamento & Precificação',
      icon: Zap,
      status: 'Em breve',
      badgeColor: 'bg-gray-100 text-gray-600'
    },
    {
      id: 'carteira',
      name: 'Carteira de Obras & PCP',
      icon: Layers,
      status: 'Em breve',
      badgeColor: 'bg-gray-100 text-gray-600'
    }
  ];

  const sections = [
    { id: 'visao-geral', title: '1. Visão Geral (3 Níveis)', icon: BookOpen },
    { id: 'cadastro-produto', title: '2. Cadastrar Produto do Zero', icon: Plus },
    { id: 'popular-estoque', title: '3. Popular Estoque (10 Chapas)', icon: Archive },
    { id: 'etiquetas-qr', title: '4. Etiquetas e QR Code', icon: QrCode },
    { id: 'retalhos-sobras', title: '5. Pedaços e Retalhos de MDF', icon: Scissors },
    { id: 'extrato-kardex', title: '6. Extrato Kardex e Auditoria', icon: History },
    { id: 'faq', title: '7. Perguntas Frequentes', icon: HelpCircle }
  ];

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto space-y-6">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-emerald-800 via-teal-900 to-slate-900 rounded-2xl p-6 sm:p-8 text-white shadow-lg relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-700/60 border border-emerald-500/40 text-xs font-semibold uppercase tracking-wider mb-2">
              <BookOpen className="w-3.5 h-3.5" />
              Documentação Oficial & Manuais de Operação
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Manuais do Sistema Santiago
            </h1>
            <p className="text-sm text-emerald-100/90 mt-1 max-w-2xl">
              Guias didáticos passo a passo para padronização de processos da fábrica e escritório.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl text-xs sm:text-sm font-semibold flex items-center gap-2 transition-all shadow-sm"
            >
              <Printer className="w-4 h-4" />
              <span>Imprimir Manual</span>
            </button>
            <button
              onClick={() => navigate('/estoque')}
              className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 transition-all shadow-sm"
            >
              <Package className="w-4 h-4" />
              <span>Ir para o Estoque</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sidebar de Módulos e Sumário */}
        <div className="lg:col-span-4 space-y-6">
          {/* Módulos */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 px-2">
              Módulos Disponíveis
            </h3>
            <div className="space-y-1">
              {modules.map((m) => {
                const Icon = m.icon;
                const isSelected = activeModule === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => {
                      if (m.id === 'estoque') setActiveModule(m.id);
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-xl text-left transition-all ${
                      isSelected
                        ? 'bg-emerald-50 text-emerald-900 border border-emerald-200 font-bold shadow-sm'
                        : 'hover:bg-gray-50 text-gray-700 font-medium'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-xs sm:text-sm">{m.name}</span>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${m.badgeColor}`}>
                      {m.status}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sumário do Módulo Ativo */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm sticky top-24">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 px-2">
              Sumário do Manual
            </h3>
            <div className="space-y-1">
              {sections.map((sec) => {
                const Icon = sec.icon;
                const isCurrent = activeSection === sec.id;
                return (
                  <button
                    key={sec.id}
                    onClick={() => setActiveSection(sec.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-xs transition-all ${
                      isCurrent
                        ? 'bg-emerald-600 text-white font-bold shadow-sm'
                        : 'hover:bg-gray-100 text-gray-600 font-medium'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{sec.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-8 bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm space-y-8">
          {/* Seção 1: Visão Geral */}
          {activeSection === 'visao-geral' && (
            <div className="space-y-6">
              <div className="border-b pb-4">
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wide">Capítulo 1</span>
                <h2 className="text-2xl font-black text-gray-900 mt-1">
                  Visão Geral da Arquitetura do Estoque
                </h2>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                  Na Marcenaria Santiago, o estoque não se resume a contar unidades soltas. Ele é organizado em 
                  <strong> 3 níveis hierárquicos</strong> para permitir que chapas inteiras, ferragens miúdas e sobras cortadas tenham controle contábil e rastreabilidade individual.
                </p>
              </div>

              {/* Infográfico dos 3 Níveis */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <div className="bg-gradient-to-b from-blue-50 to-white p-5 rounded-2xl border border-blue-200 space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-xs">
                    N1
                  </div>
                  <h4 className="font-bold text-gray-900 text-sm">Produto Mestre</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    É a ideia no catálogo geral. Define o Nome, a Categoria, a Marca, o Setor e a foto da matéria-prima.
                  </p>
                  <div className="text-[11px] font-mono bg-white p-1.5 rounded border border-blue-200 text-blue-800">
                    Ex: "MDF Louro Freijó"
                  </div>
                </div>

                <div className="bg-gradient-to-b from-purple-50 to-white p-5 rounded-2xl border border-purple-200 space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-600 text-white flex items-center justify-center font-bold text-xs">
                    N2
                  </div>
                  <h4 className="font-bold text-gray-900 text-sm">Variante / Medidas</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    É a especificação técnica: espessura em mm, cor, tamanho, acabamento, preço de custo, venda e fornecedores.
                  </p>
                  <div className="text-[11px] font-mono bg-white p-1.5 rounded border border-purple-200 text-purple-800">
                    Ex: "15mm - 275x185cm"
                  </div>
                </div>

                <div className="bg-gradient-to-b from-amber-50 to-white p-5 rounded-2xl border border-amber-200 space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-600 text-white flex items-center justify-center font-bold text-xs">
                    N3
                  </div>
                  <h4 className="font-bold text-gray-900 text-sm">Saldo & Rastreio Físico</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    As peças reais no chão de fábrica: chapas inteiras com QR Code ou pedaços cortados com etiqueta RTL-...
                  </p>
                  <div className="text-[11px] font-mono bg-white p-1.5 rounded border border-amber-200 text-amber-800">
                    Ex: "10 chapas + 3 sobras"
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 flex items-start gap-3 text-xs text-gray-600">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-gray-900">Por que essa divisão é fundamental?</strong>
                    <p className="mt-0.5">
                      Permite ao sistema alimentar os planos de corte, as fichas de orçamento de ambientes e o livro-caixa sem misturar matéria-prima nova com retalhos de projetos passados.
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-300 flex items-start gap-3 text-xs text-amber-900">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-amber-950 font-bold uppercase tracking-wider">Regra de Ouro: Vínculo Obrigatório de Setor</strong>
                    <p className="mt-0.5 text-amber-800">
                      Todo produto cadastrado DEVE ter um <strong>Setor da Fábrica</strong> vinculado (MDF's, Ferragens, Fitas, etc). Se ficar sem setor, ele se torna invisível nas bancadas operacionais!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Seção 2: Cadastrar Produto */}
          {activeSection === 'cadastro-produto' && (
            <div className="space-y-6">
              <div className="border-b pb-4">
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wide">Capítulo 2</span>
                <h2 className="text-2xl font-black text-gray-900 mt-1">
                  Passo a Passo: Cadastrar um Produto do Zero
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  Como registrar uma matéria-prima ou insumo completo no sistema.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-bold text-gray-900 text-sm">Acessar a Tela de Estoque</h4>
                    <p className="text-xs text-gray-600">
                      No menu lateral esquerdo, clique em <strong>Estoque</strong> (`/estoque`). No canto superior direito, clique no botão azul <strong>`+ Novo Produto`</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <div className="space-y-1 w-full">
                    <h4 className="font-bold text-gray-900 text-sm">Preencher os Dados Mestres do Produto</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-1">
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900 block text-xs">Nome do Produto *</span>
                        <span className="text-gray-500 text-xs">Ex: "MDF Louro Freijó"</span>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900 block text-xs">Categoria *</span>
                        <span className="text-gray-500 text-xs">Selecione "MDF" (SKU automático: MDF-0015)</span>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900 block text-xs">Marca</span>
                        <span className="text-gray-500 text-xs">Ex: Duratex, Arauco, Rometal</span>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900 block text-xs">Unidade de Medida *</span>
                        <span className="text-gray-500 text-xs">Ex: Chapa, Unid, Barra 3m</span>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-lg border-2 border-amber-300 sm:col-span-2">
                        <span className="font-bold text-amber-900 block text-xs flex items-center gap-1.5">
                          Setor da Fábrica * <span className="bg-amber-200 text-amber-900 px-1.5 py-0.2 rounded text-[10px] uppercase font-black">Obrigatório</span>
                        </span>
                        <span className="text-amber-800 text-xs block mt-0.5">
                          Selecione <strong>"MDF's e Chapas"</strong>. Se deixar sem setor, o produto não aparece nos filtros das bancadas de corte e impede contagens de inventário!
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <div className="space-y-1 w-full">
                    <h4 className="font-bold text-gray-900 text-sm">Configurar a Variante e o Motor de Cálculo por Metro Quadrado (m²)</h4>
                    <p className="text-xs text-gray-600 mb-2">
                      Na seção inferior <strong>Variantes do Produto</strong>, clique em <strong>`+ Adicionar Variante`</strong>:
                    </p>
                    
                    {/* Painel Didático de Cálculo de m² */}
                    <div className="bg-blue-50/60 p-4 rounded-xl border border-blue-200 space-y-3 text-xs">
                      <div className="font-bold text-blue-950 flex items-center gap-2">
                        <span>📐 Como o Sistema Converte a Chapa em Metro Quadrado (m²):</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-gray-700 bg-white p-2.5 rounded-lg border border-blue-100">
                        <div><strong>Espessura:</strong> 15 mm</div>
                        <div><strong>Comprimento:</strong> 275 cm</div>
                        <div><strong>Largura:</strong> 185 cm</div>
                        <div><strong>Cor:</strong> Louro Freijó</div>
                      </div>

                      {/* Caixa de Fórmula */}
                      <div className="p-3 bg-gradient-to-r from-blue-900 to-indigo-900 text-white rounded-lg space-y-1.5">
                        <div className="font-bold text-xs text-blue-200">Fórmula de Conversão Industrial:</div>
                        <div className="font-mono text-sm">
                          Área (m²) = (275cm × 185cm) ÷ 10.000 = <strong className="text-emerald-400 font-bold">5,0875 m²</strong>
                        </div>
                        <div className="text-[11px] text-blue-200">
                          (Para chapas Berneck de 280×185cm, o sistema calcula exatamente 5,1800 m²).
                        </div>
                      </div>

                      {/* Derivações de Custo e Venda por m² */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                        <div className="p-2.5 bg-white rounded border border-blue-200 space-y-1">
                          <span className="font-semibold text-gray-900 block">Preço de Custo Informado: R$ 220,00</span>
                          <span className="text-blue-700 font-mono text-xs block font-bold">
                            ➔ Custo Real: R$ 220 ÷ 5,0875 = R$ 43,24 / m²
                          </span>
                        </div>
                        <div className="p-2.5 bg-white rounded border border-blue-200 space-y-1">
                          <span className="font-semibold text-gray-900 block">Preço de Venda Informado: R$ 340,00</span>
                          <span className="text-emerald-700 font-mono text-xs block font-bold">
                            ➔ Venda Real: R$ 340 ÷ 5,0875 = R$ 66,83 / m²
                          </span>
                        </div>
                      </div>

                      {/* Dica reversa */}
                      <div className="p-2.5 bg-emerald-50 rounded border border-emerald-200 text-emerald-900 text-xs">
                        <strong>💡 Precificação Reversa:</strong> Se você quiser vender o MDF a <strong>R$ 70,00/m²</strong>, multiplique por 5,0875:
                        <div className="font-mono font-bold mt-0.5">Preço da Chapa = R$ 70,00 × 5,0875 = R$ 356,13 (digite 356,13 na venda).</div>
                      </div>

                      <div className="p-2 bg-white rounded border border-emerald-200 text-emerald-900 font-semibold flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span>Margem Bruta Calculada: 35.3% • Lucro Líquido por Chapa: R$ 120,00</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    4
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-bold text-gray-900 text-sm">Salvar o Produto</h4>
                    <p className="text-xs text-gray-600">
                      Clique no botão verde <strong>`Salvar Produto`</strong> no rodapé. O produto será adicionado ao catálogo oficial e aparecerá na lista geral.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Seção 3: Popular Estoque */}
          {activeSection === 'popular-estoque' && (
            <div className="space-y-6">
              <div className="border-b pb-4">
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wide">Capítulo 3</span>
                <h2 className="text-2xl font-black text-gray-900 mt-1">
                  Passo a Passo: Popular o Estoque (Ex: Entrada de 10 Chapas)
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  Como dar entrada física no armazém quando o caminhão de entrega descarregar na marcenaria.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">Localizar o Produto</h4>
                    <p className="text-xs text-gray-600">
                      Na tabela de produtos do Estoque, localize o <strong>MDF Louro Freijó</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">Abrir o Modal de Ajuste</h4>
                    <p className="text-xs text-gray-600">
                      Na coluna de ações da linha, clique no ícone amarelo da caixa <strong>`📦 Ajustar Estoque`</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <div className="w-full space-y-2">
                    <h4 className="font-bold text-gray-900 text-sm">Preencher a Entrada</h4>
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div><strong>Variante:</strong> Louro Freijó (15mm)</div>
                      <div><strong>Armazém:</strong> Armazém Principal</div>
                      <div><strong>Tipo de Movimento:</strong> Entrada (IN)</div>
                      <div><strong>Quantidade:</strong> 10</div>
                      <div className="sm:col-span-2"><strong>Lote:</strong> LOTE-NF-4890 • <strong>Local:</strong> Cavalete A</div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    4
                  </div>
                  <div className="w-full space-y-2">
                    <h4 className="font-bold text-gray-900 text-sm">Conferir o Simulador de Saldo em Tempo Real</h4>
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between text-xs text-emerald-900 font-mono">
                      <span>Saldo Atual: <strong>0 un</strong></span>
                      <span>➔ Movimento: <strong>+ 10 un</strong></span>
                      <span>➔ Saldo Projetado: <strong className="text-sm">10 un ✓</strong></span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    5
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">Salvar e Concluir</h4>
                    <p className="text-xs text-gray-600">
                      Clique em <strong>`Salvar Ajuste`</strong>. O saldo físico passa a ser 10 chapas, a movimentação entra no Kardex e as 10 etiquetas com números de série únicos são geradas.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Seção 4: Etiquetas e QR Code */}
          {activeSection === 'etiquetas-qr' && (
            <div className="space-y-6">
              <div className="border-b pb-4">
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wide">Capítulo 4</span>
                <h2 className="text-2xl font-black text-gray-900 mt-1">
                  O Ciclo de Códigos Únicos e Impressão de Etiquetas
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  Como cada chapa física ganha um número de série rastreável e como imprimir para etiquetar no depósito.
                </p>
              </div>

              <div className="space-y-4 text-xs text-gray-600">
                <p>
                  Ao dar entrada de 10 chapas, o sistema não cria apenas um número 10 genérico. Ele gera <strong>10 registros únicos no banco de dados</strong>:
                </p>

                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 font-mono space-y-1.5 text-xs text-gray-800">
                  <div className="flex justify-between"><span>Chapa 1: PROD_14-STK_88-0001</span> <span className="text-emerald-600 font-bold">QR Code STK-88-M4LX9Q-0</span></div>
                  <div className="flex justify-between"><span>Chapa 2: PROD_14-STK_88-0002</span> <span className="text-emerald-600 font-bold">QR Code STK-88-M4LX9Q-1</span></div>
                  <div className="flex justify-between text-gray-400"><span>...</span><span>...</span></div>
                  <div className="flex justify-between"><span>Chapa 10: PROD_14-STK_88-0010</span> <span className="text-emerald-600 font-bold">QR Code STK-88-M4LX9Q-9</span></div>
                </div>

                <div className="space-y-2 pt-2">
                  <h4 className="font-bold text-gray-900 text-sm">Como Imprimir as Etiquetas:</h4>
                  <ol className="list-decimal list-inside space-y-1.5 leading-relaxed">
                    <li>Na linha do produto, clique no ícone do olho <strong>`👁️ Ver Detalhes`</strong>.</li>
                    <li>Na aba <strong>Variantes</strong>, clique no botão azul com o ícone de <strong>Impressora</strong>.</li>
                    <li>O modal de etiquetas exibe a grade com todas as peças físicas em estoque.</li>
                    <li>Clique em <strong>`Imprimir Todas`</strong> para imprimir o rolo contínuo de 50x50mm na impressora térmica, ou clique em uma etiqueta individual.</li>
                    <li>Cole a etiqueta adesiva na quina da chapa de MDF.</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* Seção 5: Pedaços e Retalhos */}
          {activeSection === 'retalhos-sobras' && (
            <div className="space-y-6">
              <div className="border-b pb-4">
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wide">Capítulo 5</span>
                <h2 className="text-2xl font-black text-gray-900 mt-1">
                  Como Lidar com Pedaços e Sobras (Retalhos com QR Code)
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  Economize milhares de reais em madeira aproveitando as sobras úteis de cortes anteriores.
                </p>
              </div>

              {/* O Cenário Real */}
              <div className="p-4 bg-amber-50/80 border border-amber-200 rounded-xl space-y-2 text-xs">
                <h4 className="font-bold text-amber-900 flex items-center gap-1.5 text-sm">
                  <Scissors className="w-4 h-4 text-amber-700" />
                  O Caso Prático do Marceneiro:
                </h4>
                <p className="text-amber-800">
                  Uma chapa de 275x185cm foi cortada para um guarda-roupa e <strong>sobrou um pedaço de 150cm x 82cm</strong>.
                  Em vez de deixar jogado no chão para estragar, o marceneiro cadastra a sobra no sistema em 10 segundos:
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">Abrir o Banco de Retalhos</h4>
                    <p className="text-xs text-gray-600">
                      No topo da tela de Estoque, clique no botão marrom com a tesoura <strong>`Sobras / Retalhos MDF`</strong>. Em seguida, clique em <strong>`+ Cadastrar Sobra / Pedaço`</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <div className="w-full space-y-2">
                    <h4 className="font-bold text-gray-900 text-sm">Informar as Medidas com a Trena</h4>
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                      <div><strong>Chapa:</strong> MDF Louro Freijó 15mm</div>
                      <div><strong>Comprimento:</strong> 150 cm</div>
                      <div><strong>Largura:</strong> 82 cm</div>
                      <div><strong>Sentido do Veio:</strong> Longitudinal</div>
                      <div className="sm:col-span-2"><strong>Localização:</strong> Cavalete A - Baia 2</div>
                    </div>
                    <div className="p-2 bg-emerald-50 text-emerald-900 rounded border border-emerald-200 text-xs font-semibold flex items-center justify-between flex-wrap gap-2">
                      <span>Área calculada: 1,2300 m² (~0,24 chapa)</span>
                      <span className="text-emerald-700 font-bold bg-white px-2 py-0.5 rounded border border-emerald-200">
                        Valor recuperado: R$ 82,20 (com base em R$ 66,83/m²)
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-bold text-gray-900 text-sm">Salvar e Imprimir a Etiqueta RTL-...</h4>
                    <p className="text-xs text-gray-600">
                      O sistema batiza a peça automaticamente com o código único:  
                      <code className="bg-gray-100 px-2 py-0.5 rounded text-amber-800 font-bold font-mono">
                        RTL-MDFLOURO-15-150X82-K9B2
                      </code>
                    </p>
                    <p className="text-xs text-gray-500">
                      A etiqueta com QR Code é impressa, o marceneiro cola no pedaço e guarda na Baia 2.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                    4
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-bold text-gray-900 text-sm">Uso no Próximo Projeto</h4>
                    <p className="text-xs text-gray-600">
                      Quando outro marceneiro for cortar uma gaveta pequena, ele abre o painel de retalhos, encontra a peça, clica em <strong>`Baixar`</strong> e o item é consumido, <strong>evitando estragar uma chapa nova de R$ 340,00!</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Seção 6: Extrato Kardex */}
          {activeSection === 'extrato-kardex' && (
            <div className="space-y-6">
              <div className="border-b pb-4">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wide">Capítulo 6</span>
                <h2 className="text-2xl font-black text-gray-900 mt-1">
                  Extrato Kardex e Auditoria de Movimentações
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  Histórico fiscal e rastreabilidade total: saiba quem, quando e onde cada parafuso ou chapa foi movimentada.
                </p>
              </div>

              <div className="space-y-4 text-xs">
                <p className="text-gray-600">
                  Na tabela de produtos, basta clicar no ícone de histórico azul <strong>`🕒 Extrato Kardex`</strong>.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl space-y-1">
                    <span className="font-bold text-emerald-900 block">Entrada (IN)</span>
                    <p className="text-emerald-700">Chegada de mercadoria de nota fiscal, compra ou devolução de sobra física.</p>
                  </div>
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl space-y-1">
                    <span className="font-bold text-red-900 block">Saída (OUT)</span>
                    <p className="text-red-700">Retirada física de matéria-prima para a bancada de corte ou descarte por avaria.</p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl space-y-1">
                    <span className="font-bold text-blue-900 block">Transferência (TRANSFER)</span>
                    <p className="text-blue-700">Movimentação física entre armazéns (ex: do Depósito Geral para a Oficina).</p>
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-1">
                    <span className="font-bold text-amber-900 block">Ajuste (AJUSTE)</span>
                    <p className="text-amber-700">Correção de contagem física periódica de inventário.</p>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 border rounded-xl text-gray-600 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>Produtos que já possuem movimentações registradas no Kardex têm <strong>exclusão protegida pelo banco</strong> contra sumiço acidental de dados.</span>
                </div>
              </div>
            </div>
          )}

          {/* Seção 7: FAQ */}
          {activeSection === 'faq' && (
            <div className="space-y-6">
              <div className="border-b pb-4">
                <span className="text-xs font-bold text-purple-600 uppercase tracking-wide">Capítulo 7</span>
                <h2 className="text-2xl font-black text-gray-900 mt-1">
                  Perguntas Frequentes (FAQ)
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  Respostas rápidas para dúvidas comuns da equipe no dia a dia.
                </p>
              </div>

              <div className="space-y-3 text-xs">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-1">
                  <h4 className="font-bold text-amber-900 text-sm">O sistema me obriga a escolher o Setor ao cadastrar um produto?</h4>
                  <p className="text-amber-800">
                    <strong>Sim!</strong> O campo <strong>Setor *</strong> é obrigatório. Sem o setor vinculado, a mercadoria fica invisível nos filtros de fábrica e impede a realização de contagens de inventário rotativo por bancada.
                  </p>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-1">
                  <h4 className="font-bold text-blue-900 text-sm">Como calcular o preço da chapa se o fornecedor só me passar o valor do m²?</h4>
                  <p className="text-blue-800">
                    Basta multiplicar o valor do metro quadrado pela área total da chapa (ex: <strong>R$ 43,24 × 5,0875 m² = R$ 220,00</strong>) e inserir no campo Preço de Custo. O sistema calculará e confirmará os R$ 43,24/m² no painel azul dinâmico da variante!
                  </p>
                </div>

                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-1">
                  <h4 className="font-bold text-gray-900 text-sm">Posso cadastrar um retalho bem pequeno?</h4>
                  <p className="text-gray-600">
                    Sim! O sistema aceita qualquer medida em centímetros e calcula a metragem quadrada com 4 casas decimais de precisão.
                  </p>
                </div>

                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-1">
                  <h4 className="font-bold text-gray-900 text-sm">O que acontece se eu tentar dar saída de mais peças do que tenho?</h4>
                  <p className="text-gray-600">
                    O sistema bloqueia imediatamente com o aviso <em>"Estoque insuficiente"</em> e não permite saldo negativo.
                  </p>
                </div>

                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-1">
                  <h4 className="font-bold text-gray-900 text-sm">Como fechar qualquer janela ou modal rapidamente?</h4>
                  <p className="text-gray-600">
                    Basta apertar a tecla <strong>`Esc` (Escape)</strong> no teclado do computador.
                  </p>
                </div>

                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-1">
                  <h4 className="font-bold text-gray-900 text-sm">Como funciona o botão "Duplicar" na variante?</h4>
                  <p className="text-gray-600">
                    Ele copia todas as medidas e fornecedores de uma chapa em 1 clique, precisando apenas mudar a espessura ou a cor.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemManualsPage;
